// Output writers for Smart Asset Import.
//
// Produces the AssetPro bulk-import CSV using the EXACT template headers (whitespace quirks
// preserved), plus the audit/transform log and an "unmatched records" report. v1 does not
// write back to AssetPro — these files are the deliverable.

import { OUTPUT_COLUMNS } from './headerAliases';

const csvEscape = (value) => {
  if (value === null || value === undefined) return '';
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Build the AssetPro import CSV from output rows.
 * @param {Array<{output:Object}>} rows
 * @param {string[]} columns  defaults to the exact template columns
 */
export const buildImportCsv = (rows, columns = OUTPUT_COLUMNS) => {
  const header = columns.map(csvEscape).join(',');
  const body = (rows || []).map((r) =>
    columns.map((col) => csvEscape(r.output ? r.output[col] : '')).join(',')
  );
  return [header, ...body].join('\n');
};

/**
 * Build an unmatched-records report: rows whose business-critical values are not yet in
 * AssetPro master data, grouped so the user sees which masters need creating.
 */
export const buildUnmatchedReportCsv = (rows) => {
  const header = ['rowId', 'sourceRow', 'field', 'value', 'reason'];
  const lines = [header.join(',')];
  (rows || []).forEach((r) => {
    Object.keys(r.fields || {}).forEach((fk) => {
      const f = r.fields[fk];
      if (f.resolveStatus === 'UNMATCHED') {
        lines.push([
          csvEscape(r.rowId),
          csvEscape(r.sourceRowIndex),
          csvEscape(f.outputColumn),
          csvEscape(f.value),
          csvEscape(`needs new master record (${fk})`),
        ].join(','));
      }
    });
  });
  return lines.join('\n');
};

/** Trigger a client-side download of text content (CSV/JSON). */
export const downloadText = (filename, content, mime = 'text/csv;charset=utf-8;') => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
